import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { authService } from '../services/api';
import { extractErrorMessage } from '../utils/handleError';
import { useLanguage } from './LanguageContext';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const { t } = useLanguage();
  const [token, setToken] = useState(localStorage.getItem('token') || null);
  const [clid, setClid] = useState(localStorage.getItem('clid') || "CLID0000");
  const [organizationId, setOrganizationId] = useState(localStorage.getItem('organization_id') || null);
  const [clinicModules, setClinicModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;

    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      return decoded.exp < currentTime;
    } catch (error) {
      return true;
    }
  };

  // Extract clinic ID from token
  const extractClid = (token) => {
    if (!token) return "CLID0000";

    try {
      const decoded = jwtDecode(token);
      return decoded.clid || "CLID0000";
    } catch (error) {
      return "CLID0000";
    }
  };

  const extractOrganizationId = (token) => {
    if (!token) return null;
    try {
      const decoded = jwtDecode(token);
      return decoded.organization_id || null;
    } catch (error) {
      return null;
    }
  };

  // Set axios default headers with token
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Get clinic ID from token and save to state/localStorage
      const decodedClid = extractClid(token);
      const decodedOrganizationId = extractOrganizationId(token);
      setClid(decodedClid);
      setOrganizationId(decodedOrganizationId);
      localStorage.setItem('clid', decodedClid);
      if (decodedOrganizationId) {
        localStorage.setItem('organization_id', decodedOrganizationId);
      } else {
        localStorage.removeItem('organization_id');
      }
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Load user data if token exists and is valid
  useEffect(() => {
    const loadUser = async () => {
      if (token && !isTokenExpired(token)) {
        try {
          const userData = await authService.getCurrentUser();
          console.log('AuthContext - User data loaded:', userData);
          console.log('AuthContext - User role:', userData?.role);
          setCurrentUser(userData);
          // Set clinic ID from user data
          setClid(userData.clid || "CLID0000");
          setOrganizationId(userData.organization_id || extractOrganizationId(token));
          localStorage.setItem('clid', userData.clid || "CLID0000");
          if (userData.organization_id || extractOrganizationId(token)) {
            localStorage.setItem('organization_id', userData.organization_id || extractOrganizationId(token));
          }

          // Fetch clinic modules
          try {
            const modulesData = await authService.getClinicModules();
            setClinicModules(modulesData.modules || []);
          } catch (moduleError) {
            console.error('Error loading clinic modules:', moduleError);
            setClinicModules([]);
          }
        } catch (error) {
          console.error('Error loading user:', error);
          logout();
        }
      } else if (token) {
        // Token exists but is expired
        logout();
      }
      setLoading(false);
    };

    loadUser();
  }, [token]);

  // Login function
  const login = async (username, password, inputClid = "CLID0000") => {
    try {
      setError(null);
      const data = await authService.login(username, password, inputClid);
      const { access_token } = data;

      localStorage.setItem('token', access_token);
      setToken(access_token);

      // Extract and save clinic ID
      const decodedClid = extractClid(access_token);
      setClid(decodedClid);
      localStorage.setItem('clid', decodedClid);

      return true;
    } catch (error) {
      console.error('Login error:', error);
      setError(extractErrorMessage(error, 'Login failed. Please try again.', t));
      return false;
    }
  };

  // Register function
  const register = async (username, email, password, inputClid = "CLID0000") => {
    try {
      setError(null);
      await authService.register(username, email, password, inputClid);
      return true;
    } catch (error) {
      console.error('Registration error:', error);
      setError(extractErrorMessage(error, 'Registration failed. Please try again.', t));
      return false;
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('organization_id');
    setToken(null);
    setCurrentUser(null);
    setOrganizationId(null);
  };

  // Helper function to check if a module is enabled
  const hasModule = (moduleName) => {
    return clinicModules.includes(moduleName);
  };

  // Swap clinic function
  const swapClinic = async (clinicId) => {
    try {
      setError(null);
      const data = await authService.swapClinic(clinicId);
      const { access_token } = data;

      // Update token and clid
      localStorage.setItem('token', access_token);
      setToken(access_token);

      // Extract and save clinic ID
      const decodedClid = extractClid(access_token);
      const decodedOrganizationId = extractOrganizationId(access_token);
      setClid(decodedClid);
      setOrganizationId(decodedOrganizationId);
      localStorage.setItem('clid', decodedClid);
      if (decodedOrganizationId) {
        localStorage.setItem('organization_id', decodedOrganizationId);
      } else {
        localStorage.removeItem('organization_id');
      }

      // Reload user data to get updated clinic info
      try {
        const userData = await authService.getCurrentUser();
        setCurrentUser(userData);
        setClid(userData.clid || decodedClid);
        setOrganizationId(userData.organization_id || decodedOrganizationId);
        localStorage.setItem('clid', userData.clid || decodedClid);
        if (userData.organization_id || decodedOrganizationId) {
          localStorage.setItem('organization_id', userData.organization_id || decodedOrganizationId);
        }

        // Reload clinic modules
        try {
          const modulesData = await authService.getClinicModules();
          setClinicModules(modulesData.modules || []);
        } catch (moduleError) {
          console.error('Error loading clinic modules:', moduleError);
          setClinicModules([]);
        }
      } catch (userError) {
        console.error('Error reloading user after clinic swap:', userError);
      }

      return true;
    } catch (error) {
      console.error('Swap clinic error:', error);
      setError(extractErrorMessage(error, 'Failed to swap clinic. Please try again.', t));
      throw error;
    }
  };

  const value = {
    currentUser,
    token,
    clid,
    organizationId,
    clinicModules,
    hasModule,
    loading,
    error,
    login,
    register,
    logout,
    swapClinic,
    setClid,
    isAuthenticated: !!currentUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
